import { FC, useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useQuery } from "react-query";
import axios from "axios";
import { Loader } from "../Loader/Loader";
import "./LinkedInCallback.scss";

export const LinkedInCallback: FC = () => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);

  const code = searchParams.get("code");
  const licenseCode = searchParams.get("state"); // LinkedIn passes our state back

  const [status, setStatus] = useState<string>("Processing LinkedIn share...");

  const redirectUri = `${process.env.REACT_APP_EXPERIENCE_BASE_URL}/linkedin-callback`;

  const { data: tokenData, error: tokenError } = useQuery({
    queryKey: ["linkedin", "getToken", code],
    queryFn: async () => {
      const { data } = await axios.post(
        `${process.env.REACT_APP_AI_BASE_URL}/linkedin/get-token`,
        {
          grant_type: "authorization_code",
          code,
          client_secret: process.env.REACT_APP_LINKEDIN_CLIENT_SECRET,
          client_id: process.env.REACT_APP_LINKEDIN_CLIENT_ID,
          redirect_uri: redirectUri,
        }
      );
      return data;
    },
    enabled: !!code,
    retry: 1,
    onError: () => {
      setStatus("Failed to authenticate with LinkedIn. Redirecting back...");
    },
  });

  const { data: userData, error: userError } = useQuery({
    queryKey: ["linkedin", "getUserInfo", tokenData?.access_token],
    queryFn: async () => {
      const { data } = await axios.post(
        `${process.env.REACT_APP_AI_BASE_URL}/linkedin/get-user-info`,
        {
          access_token: tokenData?.access_token,
        }
      );
      return data;
    },
    enabled: !!tokenData?.access_token,
    retry: 1,
    onError: () => {
      setStatus("Failed to get user info. Redirecting back...");
    },
  });

  const { data: imageData, error: imageError } = useQuery({
    queryKey: ["linkedin", "register-image", userData?.sub],
    queryFn: async () => {
      const { data } = await axios.post(
        `${process.env.REACT_APP_AI_BASE_URL}/linkedin/register-image`,
        {
          access_token: tokenData?.access_token,
          author: `urn:li:person:${userData?.sub}`,
        }
      );
      return data;
    },
    enabled: !!userData?.sub,
    retry: 1,
    onError: () => {
      setStatus("Failed to register image. Redirecting back...");
    },
  });

  const { data: uploadImageData, error: uploadError } = useQuery({
    queryKey: ["linkedin", "upload-image", imageData?.value?.uploadUrl],
    queryFn: async () => {
      // Get the fingerprint image from session storage (set by ShareToLinkedIn)
      const imgData = sessionStorage.getItem("linkedin-share-image");

      const { data } = await axios.post(
        `${process.env.REACT_APP_AI_BASE_URL}/linkedin/upload-image`,
        {
          access_token: tokenData?.access_token,
          upload_url: imageData?.value?.uploadUrl,
          img: imgData,
        }
      );
      return data;
    },
    enabled: !!imageData?.value?.uploadUrl,
    retry: 1,
    onError: () => {
      setStatus("Failed to upload image. Redirecting back...");
    },
  });

  const { data: postData, error: postError } = useQuery({
    queryKey: ["linkedin", "post", uploadImageData?.status],
    queryFn: async () => {
      const postText = sessionStorage.getItem("linkedin-post-text");

      const { data } = await axios.post(
        `${process.env.REACT_APP_AI_BASE_URL}/linkedin/post`,
        {
          access_token: tokenData?.access_token,
          author: `urn:li:person:${userData?.sub}`,
          image: imageData?.value?.image,
          text: postText,
        }
      );
      return data;
    },
    enabled: !!uploadImageData?.status,
    retry: 1,
    onSuccess: () => {
      setStatus("Successfully shared to LinkedIn! Redirecting back...");
    },
    onError: () => {
      setStatus("Failed to post to LinkedIn. Redirecting back...");
    },
  });

  useEffect(() => {
    // If we have a post result (success or any error), redirect back after 2 seconds
    if (
      postData ||
      tokenError ||
      userError ||
      imageError ||
      uploadError ||
      postError
    ) {
      const timer = setTimeout(() => {
        // Check if we stored where they came from
        const returnUrl = sessionStorage.getItem("linkedin-return-url");

        if (returnUrl) {
          sessionStorage.removeItem("linkedin-return-url");
          sessionStorage.removeItem("linkedin-share-image");
          sessionStorage.removeItem("linkedin-post-text");
          history.push(returnUrl);
        } else if (licenseCode) {
          sessionStorage.removeItem("linkedin-share-image");
          sessionStorage.removeItem("linkedin-post-text");
          // Fallback: use licenseCode from state parameter
          history.push(`/digital-fingerprint/${licenseCode}`);
        } else {
          // Last resort: redirect to account page
          history.push("/account");
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [
    postData,
    tokenError,
    userError,
    imageError,
    uploadError,
    postError,
    licenseCode,
    history,
  ]);

  // If no code, redirect immediately
  useEffect(() => {
    if (!code) {
      setStatus("Invalid callback. Redirecting...");
      setTimeout(() => {
        history.push("/account");
      }, 1000);
    }
  }, [code, history]);

  return (
    <div className="linkedin-callback">
      <Loader />
      <h2 className="linkedin-callback__status">{status}</h2>
      {tokenError || userError || imageError || uploadError || postError ? (
        <p className="linkedin-callback__error">
          An error occurred, but don't worry - you'll be redirected back.
        </p>
      ) : null}
    </div>
  );
};
